import axios from "axios";

/**
 * 🚀 API Service - URL RELATIVA (Best Practice)
 *
 * ✅ Elimina CORS (mesma origem)
 * ✅ Elimina redirects (browser resolve automaticamente)
 * ✅ Funciona em dev/preview/prod sem mudanças
 */

class ApiService {
  constructor() {
    this.setupAxios();
    this.pendingRequests = new Map();
    console.log("🚀 API Service: URL Relativa + Dispatcher");
  }

  setupAxios() {
    const hostname = window.location.hostname;
    const apiUrl = process.env.VUE_APP_API_URL;

    console.log("🔍 [SETUP AXIOS v5.0.0 - USA VUE_APP_API_URL]", {
      hostname,
      origin: window.location.origin,
      VUE_APP_API_URL: apiUrl,
      timestamp: new Date().toISOString(),
    });

    // 🎯 PRIORIDADE: Usar VUE_APP_API_URL se definido
    if (apiUrl) {
      axios.defaults.baseURL = apiUrl;
      console.log("✅ Axios: URL da variável VUE_APP_API_URL →", apiUrl);
    } else if (hostname === "www.blogdavid.cv") {
      // Produção - FORÇA URL absoluta com www
      axios.defaults.baseURL = "https://www.blogdavid.cv";
      console.log(
        "✅ Axios: URL ABSOLUTA FORÇADA com WWW →",
        axios.defaults.baseURL
      );
    } else if (hostname === "blogdavid.cv") {
      // Redirect imediato para www
      console.log("🔄 REDIRECT: blogdavid.cv → www.blogdavid.cv");
      window.location.href =
        "https://www.blogdavid.cv" +
        window.location.pathname +
        window.location.search +
        window.location.hash;
      return;
    } else {
      // Localhost e preview - URL relativa
      axios.defaults.baseURL = "";
      console.log("✅ Axios: URL relativa (dev/preview)");
    }

    axios.defaults.withCredentials = true;

    // Validação final
    console.log("🎯 [AXIOS FINAL]", {
      baseURL: axios.defaults.baseURL,
      fromEnv: !!apiUrl,
      testeCompleto:
        (axios.defaults.baseURL || window.location.origin) + "/api/handlers",
    });
  }

  async request(method, route, data = null, config = {}) {
    console.log("🚀 REQUEST:", method, route);

    const requestKey = method + ":" + route;
    if (
      method.toUpperCase() === "GET" &&
      this.pendingRequests.has(requestKey)
    ) {
      console.log("♻️ Request já em andamento:", requestKey);
      return this.pendingRequests.get(requestKey);
    }

    const maxRetries = config.retries || 2;
    const retryDelay = config.retryDelay || 1000;

    const makeRequest = async (attempt = 0) => {
      try {
        const timeout = attempt === 0 ? 45000 : 30000;

        const axiosConfig = {
          method,
          url: route,
          timeout,
          withCredentials: true,
        };

        if (config.headers) {
          axiosConfig.headers = config.headers;
        }

        // ✅ Adicionar params do config (query string)
        if (config.params) {
          axiosConfig.params = config.params;
        }

        if (data) {
          if (method.toUpperCase() === "GET") {
            axiosConfig.params = data;
          } else {
            axiosConfig.data = data;
          }
        }

        const response = await axios(axiosConfig);

        if (method.toUpperCase() === "GET") {
          this.pendingRequests.delete(requestKey);
        }

        return response.data;
      } catch (error) {
        if (method.toUpperCase() === "GET") {
          this.pendingRequests.delete(requestKey);
        }

        if (attempt < maxRetries && this.shouldRetry(error)) {
          console.warn(
            "⚠️ Tentativa " +
              (attempt + 1) +
              " falhou, tentando novamente em " +
              retryDelay +
              "ms...",
            {
              error: error.message,
              code: error.code,
              status: error.response ? error.response.status : undefined,
            }
          );

          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          return makeRequest(attempt + 1);
        }

        console.error("❌ Request error " + method + " " + route + ":", error);
        throw error;
      }
    };

    const promise = makeRequest();

    if (method.toUpperCase() === "GET") {
      this.pendingRequests.set(requestKey, promise);
    }

    return promise;
  }

  shouldRetry(error) {
    return (
      error.code === "ECONNABORTED" ||
      error.code === "ERR_NETWORK" ||
      (error.message && error.message.includes("timeout")) ||
      (error.response && error.response.status === 500)
    );
  }

  get(route, config = {}) {
    return this.request("GET", route, config.params, config);
  }

  post(route, data, config = {}) {
    return this.request("POST", route, data, config);
  }

  put(route, data, config = {}) {
    return this.request("PUT", route, data, config);
  }

  delete(route, config = {}) {
    return this.request("DELETE", route, null, config);
  }
}

export default new ApiService();
